import { useState } from 'react';
import { Check, Copy, MessageCircle, Share2 } from 'lucide-react';
import { buildWhatsAppUrl } from '@peles/shared';

export interface ShareLinkButtonProps {
  /** Absolute URL to share. */
  url: string;
  /** Message prefix used for WhatsApp and the native share sheet. */
  text?: string;
  /** Title for the native share sheet. */
  title?: string;
  /** Address the WhatsApp message to this number; omit for the contact picker. */
  phone?: string | null;
  /**
   * Who/what the link is for ("Sarah Levi"). Used to keep accessible names
   * unique when several share controls sit in one list.
   */
  label?: string;
  /** Compact icon-only row (for table rows) instead of labeled buttons. */
  compact?: boolean;
  /** Themes the labeled buttons; defaults to the app primary color. */
  accentColor?: string;
  className?: string;
}

/**
 * Copy / WhatsApp / native-share control for any public link.
 *
 * Domain-agnostic on purpose: guest invitations, questionnaires, vendor
 * uploads — anything with a shareable URL can drop this in. The native share
 * sheet is offered only where the browser supports it (mobile), so desktop
 * users still get copy + WhatsApp Web.
 */
export function ShareLinkButton({
  url,
  text,
  title,
  phone,
  label,
  compact = false,
  accentColor,
  className = '',
}: ShareLinkButtonProps) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const handleCopy = async () => {
    setCopyFailed(false);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is blocked on insecure origins and in some in-app
      // browsers — say so instead of silently doing nothing.
      setCopyFailed(true);
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(text ? `${text} ${url}` : url);
    const base = buildWhatsAppUrl(phone) ?? 'https://wa.me/';
    window.open(`${base}?text=${message}`, '_blank', 'noopener');
  };

  const copyLabel = label ? `Copy link for ${label}` : 'Copy link';
  const whatsAppLabel = label ? `Share via WhatsApp with ${label}` : 'Share via WhatsApp';

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleNativeShare = () => {
    void navigator.share({ title, text, url }).catch(() => {
      // The guest dismissed the share sheet — nothing to report.
    });
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <button
          type="button"
          title={copyLabel}
          aria-label={copyLabel}
          onClick={() => void handleCopy()}
          className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
        >
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
        </button>
        <button
          type="button"
          title={whatsAppLabel}
          aria-label={whatsAppLabel}
          onClick={handleWhatsApp}
          className="p-1.5 text-gray-400 hover:text-green-600 rounded"
        >
          <MessageCircle className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const buttonStyle = accentColor ? { borderColor: accentColor, color: accentColor } : undefined;

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-label={copyLabel}
          onClick={() => void handleCopy()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
          style={buttonStyle}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy link'}
        </button>
        <button
          type="button"
          aria-label={whatsAppLabel}
          onClick={handleWhatsApp}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
          style={buttonStyle}
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </button>
        {canNativeShare && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
            style={buttonStyle}
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
        )}
      </div>
      {copyFailed && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          Couldn&apos;t copy automatically — the link is {url}
        </p>
      )}
    </div>
  );
}
